import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';

const SESSION_KEY = 'hemma_popup_shown';

const POSITION_STYLES = {
  'center':        { display: 'flex', alignItems: 'center',    justifyContent: 'center' },
  'top-center':    { display: 'flex', alignItems: 'flex-start',justifyContent: 'center',  paddingTop: '80px' },
  'bottom-center': { display: 'flex', alignItems: 'flex-end',  justifyContent: 'center',  paddingBottom: '32px' },
  'top-left':      { display: 'flex', alignItems: 'flex-start',justifyContent: 'flex-end',padding: '80px 24px 24px' },
  'top-right':     { display: 'flex', alignItems: 'flex-start',justifyContent: 'flex-start',padding: '80px 24px 24px' },
  'bottom-left':   { display: 'flex', alignItems: 'flex-end',  justifyContent: 'flex-end',  padding: '24px' },
  'bottom-right':  { display: 'flex', alignItems: 'flex-end',  justifyContent: 'flex-start',padding: '24px' },
};

const ENTER_TRANSFORMS = {
  fadeIn:    { from: 'scale(0.95)', to: 'scale(1)' },
  slideDown: { from: 'translateY(-48px) scale(0.97)', to: 'translateY(0) scale(1)' },
  slideUp:   { from: 'translateY(48px) scale(0.97)',  to: 'translateY(0) scale(1)' },
  zoom:      { from: 'scale(0.6)',  to: 'scale(1)' },
  bounce:    { from: 'scale(0.4)',  to: 'scale(1)' },
};

const BTN_BASE = {
  primary:   { background: 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40,227,218,0.5) 0%, rgba(40,227,218,0) 100%), rgba(40,227,218,0.25)', border: '1px solid rgba(40,227,218,0.4)', color: '#fff' },
  secondary: { background: 'rgba(255,255,255,0.1)',  border: '1px solid rgba(255,255,255,0.2)',  color: '#fff' },
  danger:    { background: 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227,40,90,0.5) 0%, rgba(227,40,90,0) 100%), rgba(227,40,90,0.25)',  border: '1px solid rgba(227,40,90,0.4)',  color: '#fff' },
  ghost:     { background: 'transparent',            border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.75)' },
};

function injectKeyframes() {
  if (document.getElementById('hemma-popup-styles')) return;
  const style = document.createElement('style');
  style.id = 'hemma-popup-styles';
  style.textContent = `
    @keyframes hemma-shake {
      0%,100%{transform:translateX(0)}
      20%{transform:translateX(-6px)}
      40%{transform:translateX(6px)}
      60%{transform:translateX(-4px)}
      80%{transform:translateX(4px)}
    }
    @keyframes hemma-glow {
      0%,100%{box-shadow:0 0 4px rgba(40,227,218,0.4)}
      50%{box-shadow:0 0 18px rgba(40,227,218,0.9)}
    }
    .hemma-btn-shake { animation: hemma-shake 0.6s ease infinite; }
    .hemma-btn-glow  { animation: hemma-glow  1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}

const BTN_ANIM_CLASS = {
  none:   '',
  pulse:  'animate-pulse',
  bounce: 'animate-bounce',
  shake:  'hemma-btn-shake',
  glow:   'hemma-btn-glow',
};

export default function AnnouncementPopup() {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    injectKeyframes();
    let mounted = true;

    fetch(`${API_BASE}/popup-settings`, { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(data => {
        if (!mounted || !data?.enabled) return;

        if (data.show_once_per_session) {
          try {
            if (sessionStorage.getItem(SESSION_KEY)) return;
            sessionStorage.setItem(SESSION_KEY, '1');
          } catch {}
        }

        setSettings(data);
        const delay = Math.max(0, Number(data.delay_seconds || 0)) * 1000;

        timerRef.current = setTimeout(() => {
          if (!mounted) return;
          setOpen(true);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            if (mounted) setVisible(true);
          }));
        }, delay);
      })
      .catch(() => {});

    return () => {
      mounted = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(() => setOpen(false), 380);
  };

  if (!open || !settings) return null;

  const posStyle = POSITION_STYLES[settings.position] || POSITION_STYLES['center'];
  const tf = ENTER_TRANSFORMS[settings.animation] || ENTER_TRANSFORMS.fadeIn;
  const easing = settings.animation === 'bounce'
    ? 'cubic-bezier(0.34,1.7,0.64,1)'
    : 'cubic-bezier(0.22,1,0.36,1)';

  const boxTransition = { transition: `opacity 0.35s ease, transform 0.4s ${easing}` };
  const boxHidden  = { opacity: 0, transform: tf.from };
  const boxVisible = { opacity: 1, transform: tf.to  };

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, ...posStyle }}
      onClick={close}
    >
      {             }
      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(8,20,35,0.65)',
          backdropFilter: settings.backdrop_blur ? 'blur(8px)' : 'none',
          transition: 'opacity 0.35s ease',
          opacity: visible ? 1 : 0,
        }}
      />

      {             }
      <div
        dir="rtl"
        style={{
          position: 'relative',
          width: '90vw',
          maxWidth: '480px',
          borderRadius: '18px',
          padding: '28px 24px 24px',
          background: 'linear-gradient(0deg,rgba(255,255,255,0.02),rgba(255,255,255,0.02)),radial-gradient(92.12% 184.55% at 50% -55%,rgba(40,227,218,0.2) 0%,rgba(40,227,218,0) 100%),#081423',
          border: '1px solid rgba(40,227,218,0.28)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(40,227,218,0.08)',
          ...boxTransition,
          ...(visible ? boxVisible : boxHidden),
        }}
        onClick={e => e.stopPropagation()}
      >
        {                }
        <button
          onClick={close}
          aria-label="إغلاق"
          style={{
            position: 'absolute', top: '12px', left: '12px',
            width: '28px', height: '28px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer', transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        {             }
        {settings.title && (
          <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem', marginBottom: '10px', paddingLeft: '32px', textAlign: 'right', lineHeight: 1.4 }}>
            {settings.title}
          </h2>
        )}

        {          }
        {settings.body && (
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: settings.buttons?.length ? '20px' : 0, textAlign: 'right' }}>
            {settings.body}
          </p>
        )}

        {             }
        {settings.buttons?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
            {settings.buttons.map((btn, i) => (
              <button
                key={btn.id || i}
                className={BTN_ANIM_CLASS[btn.animation] || ''}
                style={{
                  padding: '8px 18px',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  ...BTN_BASE[btn.style] || BTN_BASE.primary,
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                onClick={() => {
                  if (btn.action === 'close')    { close(); }
                  else if (btn.action === 'url' && btn.url)      { window.open(btn.url, '_blank', 'noopener,noreferrer'); }
                  else if (btn.action === 'navigate' && btn.url) { window.location.href = btn.url; }
                  else close();
                }}
              >
                {btn.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
